/**
 * Profile → "This iPhone": notifications and the Face ID lock for the Defera
 * app on this phone (src/native/push.js, src/native/faceId.js). Both are per
 * phone, so a customer with two phones sets each one, and both reset at
 * sign-out.
 *
 * Notifications off here takes this phone off the account and keeps it off
 * until turned back on; when iOS itself has them off, the row says so and
 * links to the app's page in iPhone Settings, the only place to change that.
 *
 * App only.
 */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Bell, Fingerprint, LockKeyhole, ScanFace } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { REWARDS_SECTION_CARD } from '@/constants/brandColors';
import { isNativeApp } from '@/native/bootstrap';
import { enablePushNotifications, pushPermission, pushRegisteredHere, pushTurnedOffHere, turnOffPushHere } from '@/native/push';
import { biometricAvailability, faceIdLockEnabled, setFaceIdLock, verifyOwner } from '@/native/faceId';

const LOCK_ICONS = { 'Face ID': ScanFace, 'Touch ID': Fingerprint, passcode: LockKeyhole };

function SettingRow({ icon: Icon, title, detail, checked, disabled, onChange, children }) {
  return (
    <div className="flex items-center gap-3.5 px-5 py-4 sm:px-6">
      <Icon className="h-4 w-4 flex-shrink-0 text-[hsl(228,30%,72%)]" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-normal text-white">{title}</p>
        <p className="text-[12px] font-normal leading-snug text-white/45">{detail}</p>
        {children}
      </div>
      {onChange && (
        <Switch
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
          aria-label={title}
          className="data-[state=checked]:bg-emerald-500/80 data-[state=unchecked]:bg-white/20"
        />
      )}
    </div>
  );
}

SettingRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  children: PropTypes.node,
};

export default function NativeAppSettingsCard() {
  const [permission, setPermission] = useState(null);
  const [pushOn, setPushOn] = useState(false);
  const [lock, setLock] = useState({ available: false, kind: null });
  const [lockOn, setLockOn] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const [p, bio] = await Promise.all([pushPermission(), biometricAvailability()]);
    setPermission(p);
    setPushOn(p === 'granted' && !pushTurnedOffHere() && pushRegisteredHere());
    setLock(bio);
    setLockOn(faceIdLockEnabled());
  }, []);

  useEffect(() => { if (isNativeApp) refresh(); }, [refresh]);

  if (!isNativeApp || permission === null) return null;
  const showPush = permission !== 'unavailable';
  if (!showPush && !lock.available) return null;

  const changePush = async (on) => {
    setWorking(true); setError(null);
    if (on) {
      const result = await enablePushNotifications();
      if (result === 'error') setError("Notifications couldn't be turned on just now. Please try again.");
    } else {
      await turnOffPushHere();
    }
    await refresh();
    setWorking(false);
  };

  const changeLock = async (on) => {
    setError(null);
    if (on) {
      setWorking(true);
      const ok = await verifyOwner();
      setWorking(false);
      if (!ok) return;
    }
    setFaceIdLock(on);
    setLockOn(on);
  };

  const kind = lock.kind || 'Face ID';
  const lockTitle = kind === 'passcode' ? 'Passcode lock' : `${kind} lock`;
  const lockDetail = kind === 'passcode' ? 'Ask for your passcode when Defera opens' : `Ask for ${kind} when Defera opens`;

  return (
    <div className={REWARDS_SECTION_CARD}>
      <div className="border-b border-white/[0.06] px-5 pb-3 pt-5 sm:px-6">
        <h3 className="text-[15px] font-normal tracking-tight text-white">This iPhone</h3>
        <p className="text-[11.5px] font-normal text-white/40">Settings for the Defera app on this phone</p>
      </div>
      {showPush && (
        permission === 'denied' ? (
          <SettingRow icon={Bell} title="Notifications" detail="Turned off for Defera in iPhone Settings">
            <a href="app-settings:" className="mt-1 inline-block text-[12px] font-normal text-[hsl(228,60%,78%)] underline-offset-2 hover:underline">
              Open iPhone Settings
            </a>
          </SettingRow>
        ) : (
          <SettingRow
            icon={Bell}
            title="Notifications"
            detail="Payments, flight changes and other trip updates"
            checked={pushOn}
            disabled={working}
            onChange={changePush}
          />
        )
      )}
      {lock.available && (
        <SettingRow
          icon={LOCK_ICONS[kind] || LockKeyhole}
          title={lockTitle}
          detail={lockDetail}
          checked={lockOn}
          disabled={working}
          onChange={changeLock}
        />
      )}
      {error && <p className="px-5 pb-4 text-[12px] font-normal text-red-300 sm:px-6">{error}</p>}
    </div>
  );
}
